import { AGENTS } from '../config/competition'
import { compactMoney, pct, signedCompactMoney, signedPct } from '../core/format'
import type { CompetitionState } from '../core/types'
import { HouseTag, VendorTag } from './primitives/Bits'
import { AgentAvatar } from './primitives/Media'
import { Panel } from './primitives/Panel'
import { Standby } from './primitives/Standby'
import { Sparkline } from './primitives/Sparkline'

export function AgentRoster({ state, standby }: { state: CompetitionState; standby: boolean }) {
  const rankOf = (id: string) => state.leaderboard.find((r) => r.agentId === id)?.rank ?? 0

  if (standby) {
    return (
      <Panel title="AGENTS / PORTFOLIO" note={<span className="u-label">{AGENTS.length} ENTRANTS</span>}>
        <Standby note="Equity, cash and win rate land here once the round opens." />
      </Panel>
    )
  }

  return (
    <Panel
      title="AGENTS / PORTFOLIO"
      note={<span className="u-label">{AGENTS.length} ENTRANTS</span>}
      scroll
    >
      <div className="roster">
        {AGENTS.map((a) => {
          const p = state.portfolios[a.id]
          const rank = rankOf(a.id)
          if (!p) {
            return (
              <article key={a.id} className="acard acard--empty">
                <span className="u-label">{a.name} — NO DATA</span>
              </article>
            )
          }
          const pnl = p.equity - p.startEquity
          return (
            <article key={a.id} className="acard" style={{ ['--accent' as string]: a.color }}>
              <header className="acard__head">
                <AgentAvatar agentId={a.id} size={22} />
                <span className="acard__name">{a.name}</span>
                <VendorTag vendor={a.vendor} />
                <HouseTag house={a.house} />
                {rank ? <span className="acard__rank u-num">#{rank}</span> : null}
              </header>

              <div className="acard__main">
                <div className="acard__equity">
                  <span className="acard__value u-num">{compactMoney(p.equity)}</span>
                  <span className={`acard__pnl u-num ${pnl >= 0 ? 'u-up' : 'u-down'}`}>
                    {signedCompactMoney(pnl)} <em>{signedPct(p.roiPct, 1)}</em>
                  </span>
                </div>
                <div className="acard__spark" style={{ color: a.color }}>
                  <Sparkline
                    data={p.spark}
                    color={a.color}
                    baseline={p.startEquity}
                    width={104}
                    height={34}
                  />
                </div>
              </div>

              <dl className="acard__stats">
                <div>
                  <dt>CASH</dt>
                  <dd className="u-num">{compactMoney(p.cash)}</dd>
                </div>
                <div>
                  <dt>DEPL</dt>
                  <dd className="u-num">{compactMoney(p.deployed)}</dd>
                </div>
                <div>
                  <dt>UNRL</dt>
                  <dd className={`u-num ${p.unrealized >= 0 ? 'u-up' : 'u-down'}`}>
                    {signedCompactMoney(p.unrealized)}
                  </dd>
                </div>
                <div>
                  <dt>WIN</dt>
                  <dd className="u-num">{pct(p.winRatePct, 0)}</dd>
                </div>
                <div>
                  <dt>FILLS</dt>
                  <dd className="u-num">{p.tradeCount}</dd>
                </div>
              </dl>

              <footer className="acard__foot" title={`${a.model} — ${a.doctrine}`}>
                <span className="acard__model">{a.model}</span>
                <span className="acard__doctrine">{a.doctrine}</span>
              </footer>
            </article>
          )
        })}
      </div>
    </Panel>
  )
}
